import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import mockTestService from '../services/mockTestService'
import type {
  StartTestAttemptRequest,
  UpdateQuestionResponseRequest,
  RecordQuestionEventsRequest,
  StudentAttemptsParams,
  AttemptResponsesParams,
} from '../types'

// Query keys
export const mockTestKeys = {
  all: ['mockTest'] as const,
  attempt: (attemptId: string) => [...mockTestKeys.all, 'attempt', attemptId] as const,
  responses: (attemptId: string) => [...mockTestKeys.all, 'responses', attemptId] as const,
  questions: (questionIds: string[]) => [...mockTestKeys.all, 'questions', questionIds] as const,
  question: (questionId: string) => [...mockTestKeys.all, 'question', questionId] as const,
  studentAttempts: (studentId: string) => [...mockTestKeys.all, 'studentAttempts', studentId] as const,
  studentTestAttempts: (studentId: string, testCode: string) =>
    [...mockTestKeys.all, 'studentTestAttempts', studentId, testCode] as const,
  analytics: (attemptId: string) => [...mockTestKeys.all, 'analytics', attemptId] as const,
  aggregateStats: (testCode: string) => [...mockTestKeys.all, 'aggregateStats', testCode] as const,
  leaderboard: (testCode: string) => [...mockTestKeys.all, 'leaderboard', testCode] as const,
}

/**
 * Hook to start a new test attempt
 */
export function useStartTestAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: StartTestAttemptRequest) =>
      mockTestService.startTestAttempt(request),
    onSuccess: (data) => {
      // Invalidate any existing attempt queries
      queryClient.invalidateQueries({ queryKey: mockTestKeys.all })
      return data
    },
  })
}

/**
 * Hook to submit a test attempt
 */
export function useSubmitTestAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (attemptId: string) => mockTestService.submitTestAttempt(attemptId),
    onSuccess: (_data, attemptId) => {
      queryClient.invalidateQueries({ queryKey: mockTestKeys.attempt(attemptId) })
    },
  })
}

/**
 * Hook to abandon a test attempt
 */
export function useAbandonTestAttempt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (attemptId: string) => mockTestService.abandonTestAttempt(attemptId),
    onSuccess: (_data, attemptId) => {
      queryClient.invalidateQueries({ queryKey: mockTestKeys.attempt(attemptId) })
    },
  })
}

/**
 * Hook to update question response
 */
export function useUpdateQuestionResponse(attemptId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateQuestionResponseRequest) =>
      mockTestService.updateQuestionResponse(attemptId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mockTestKeys.responses(attemptId) })
    },
  })
}

/**
 * Hook to record question events (fire-and-forget)
 */
export function useRecordQuestionEvents(attemptId: string) {
  return useMutation({
    mutationFn: (request: RecordQuestionEventsRequest) =>
      mockTestService.recordQuestionEvents(attemptId, request),
  })
}

/**
 * Hook to get test attempt details
 */
export function useTestAttempt(attemptId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: mockTestKeys.attempt(attemptId),
    queryFn: () => mockTestService.getTestAttempt(attemptId),
    staleTime: 1000 * 60, // 1 minute
    enabled: enabled && !!attemptId,
  })
}

/**
 * Hook to get all question responses for an attempt
 */
export function useAttemptResponses(attemptId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: mockTestKeys.responses(attemptId),
    queryFn: () => mockTestService.getAttemptResponses(attemptId),
    staleTime: 1000 * 30, // 30 seconds
    enabled: enabled && !!attemptId,
  })
}

/**
 * Hook to get questions by IDs (batch)
 */
export function useQuestionsBatch(questionIds: string[], enabled: boolean = true) {
  return useQuery({
    queryKey: mockTestKeys.questions(questionIds),
    queryFn: () => mockTestService.getQuestionsByIds(questionIds),
    staleTime: 1000 * 60 * 10, // 10 minutes - questions don't change
    enabled: enabled && questionIds.length > 0,
  })
}

/**
 * Hook to get a single question
 */
export function useQuestion(questionId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: mockTestKeys.question(questionId),
    queryFn: () => mockTestService.getQuestionById(questionId),
    staleTime: 1000 * 60 * 10, // 10 minutes
    enabled: enabled && !!questionId,
  })
}

/**
 * Hook to get all attempts by a student for a specific test
 */
export function useStudentAttemptsForTest(
  studentId: string,
  testCode: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: mockTestKeys.studentTestAttempts(studentId, testCode),
    queryFn: () => mockTestService.getStudentAttemptsForTest(studentId, testCode),
    staleTime: 1000 * 60, // 1 minute
    enabled: enabled && !!studentId && !!testCode,
  })
}

/**
 * Hook to get all attempts by a student (paginated)
 */
export function useStudentAttempts(
  studentId: string,
  params?: StudentAttemptsParams,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [...mockTestKeys.studentAttempts(studentId), params],
    queryFn: () => mockTestService.getStudentAttempts(studentId, params),
    staleTime: 1000 * 60, // 1 minute
    enabled: enabled && !!studentId,
  })
}

/**
 * Hook to get attempt responses with filters
 */
export function useAttemptResponsesFiltered(
  attemptId: string,
  params?: AttemptResponsesParams,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [...mockTestKeys.responses(attemptId), params],
    queryFn: () => mockTestService.getAttemptResponses(attemptId, params),
    staleTime: 1000 * 30, // 30 seconds
    enabled: enabled && !!attemptId,
  })
}

/**
 * Hook to get detailed analytics for an attempt
 */
export function useAttemptAnalytics(attemptId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: mockTestKeys.analytics(attemptId),
    queryFn: () => mockTestService.getAttemptAnalytics(attemptId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: enabled && !!attemptId,
  })
}

/**
 * Hook to get aggregate statistics for a test
 */
export function useTestAggregateStats(testCode: string, enabled: boolean = true) {
  return useQuery({
    queryKey: mockTestKeys.aggregateStats(testCode),
    queryFn: () => mockTestService.getTestAggregateStats(testCode),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: enabled && !!testCode,
  })
}

/**
 * Hook to get leaderboard for a test
 */
export function useTestLeaderboard(
  testCode: string,
  page = 0,
  size = 50,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [...mockTestKeys.leaderboard(testCode), page, size],
    queryFn: () => mockTestService.getTestLeaderboard(testCode, page, size),
    staleTime: 1000 * 60, // 1 minute
    enabled: enabled && !!testCode,
  })
}
